import React, { useState, useEffect, useMemo } from 'react';
import { HiMagnifyingGlass, HiCheckCircle, HiArrowDownTray, HiInboxStack } from 'react-icons/hi2';
import './StockShortages.scss';
import { getAllStockShortagesByLocalIdAndDateRange,
  setShortageSolved } from '../../../../../../crud/management_api/stock_management';
import { StockShortage, ProductStock, Merma } from '../../types';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import { exportStockToExcel } from '../../utils/stockExcelExport';

const StockShortages: React.FC = () => {
  const [data, setData] = useState<StockShortage[]>([]);
  const [filteredData, setFilteredData] = useState<StockShortage[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStorageUnits, setSelectedStorageUnits] = useState<Array<{ value: string | number; label: string }>>([]);
  const [selectedItems, setSelectedItems] = useState<Array<{ value: string | number; label: string }>>([]);
  
  // Calculate default dates: 7 days ago to tomorrow
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  };
  
  const getDefaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  };
  
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [resolvingShortages, setResolvingShortages] = useState<Set<number>>(new Set());

  // Derived filter options from shortage data
  const storageOptions = useMemo(() => {
    const uniqueStorages = [...new Set(data.map(item => item.storage_unit_name).filter(Boolean))];
    return uniqueStorages.map(storage => ({ value: storage, label: storage }));
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const localId = user?.local_id;
        if (!localId) {
          console.warn('Local ID no encontrado en localStorage');
          return;
        }
        
        const response = await getAllStockShortagesByLocalIdAndDateRange(
          localId, 
          startDate, 
          endDate
        );
        console.log('Raw API response:', response.data);
        setData(response.data);
      } catch (error) {
        console.error('Error fetching stock shortages:', error);
        toast.error('Error al cargar los quiebres de stock');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [startDate, endDate]);

  useEffect(() => {
    let filtered = data;

    // Filter by search text
    if (searchValue.trim()) {
      const search = searchValue.toLowerCase();
      filtered = filtered.filter(item =>
        (item.item_name && item.item_name.toLowerCase().includes(search)) ||
        (item.ingredient_name && item.ingredient_name.toLowerCase().includes(search)) ||
        (item.storage_unit_name && item.storage_unit_name.toLowerCase().includes(search)) ||
        (item.unidad_medida_name && item.unidad_medida_name.toLowerCase().includes(search))
      );
    }

    // Filter by storage units (using selectedStorages from ResponsiveFilters)
    if (selectedStorageUnits.length > 0) {
      const selectedNames = selectedStorageUnits.map(opt => opt.value);
      filtered = filtered.filter(item => selectedNames.includes(item.storage_unit_name));
    }

    // Filter by items (using selectedAcronyms from ResponsiveFilters)  
    if (selectedItems.length > 0) {
      const selectedNames = selectedItems.map(opt => opt.value);
      filtered = filtered.filter(item => 
        selectedNames.includes(item.item_name) || 
        selectedNames.includes(item.ingredient_name) ||
        selectedNames.includes(item.unidad_medida_name)
      );
    }

    // Filter by date range
    if (startDate || endDate) {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.local_timestamp);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : null;
        
        return (!start || itemDate >= start) && (!end || itemDate <= end);
      });
    }

    setFilteredData(filtered);
  }, [selectedStorageUnits, selectedItems, startDate, endDate, data, searchValue]);



  const handleResolveShortage = async (shortageId: number) => {
    setResolvingShortages(prev => new Set(prev).add(shortageId));
    
    try {
      await setShortageSolved(shortageId, {});
      setData(prev => prev.map(item => 
        item.shortage_id === shortageId ? { ...item, solved: true } : item
      ));
      toast.success('Quiebre de stock resuelto exitosamente');
    } catch (error) {
      console.error('Error al resolver quiebre de stock:', error);
      toast.error('Error al resolver el quiebre de stock');
    } finally {
      setResolvingShortages(prev => {
        const newSet = new Set(prev);
        newSet.delete(shortageId);
        return newSet;
      });
    }
  };

  // Excel Export Handler
  const handleExportToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning('No hay datos de quiebres de stock para exportar');
      return;
    }

    try {
      // Export only shortages data, empty arrays for stock and mermas
      const emptyStock: ProductStock[] = [];
      const emptyMermas: Merma[] = [];
      
      exportStockToExcel(emptyStock, filteredData, emptyMermas);
      toast.success('Excel exportado exitosamente');
    } catch (error) {
      console.error('Error exporting stock shortages to Excel:', error);
      toast.error('Error al exportar a Excel. Por favor, intenta nuevamente.');
    }
  };

  const formatTimeElapsed = (startDate: string | Date, endDate?: string | Date) => {
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    const diffMs = end.getTime() - start.getTime();
    
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}d ${diffHours}h`;
    } else if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  };

  const renderShortageList = () => {
    if (filteredData.length === 0) {
      return (
        <div className="mp-empty-state">
          <HiInboxStack className="mp-empty-state-icon" />
          <h3 className="mp-empty-state-title">Sin quiebres de stock</h3>
          <p className="mp-empty-state-text">No se han registrado quiebres de stock con los filtros aplicados.</p>
        </div>
      );
    }

    return (
      <div className="stock-list-container">
        {filteredData.map((shortage) => (
          <div key={`${shortage.shortage_id}-${shortage.storage_unit_id}`} className="stock-item-row">
            <div className="item-basic-info">
              <span className="item-name">{shortage.item_name || shortage.ingredient_name || 'Item desconocido'}</span>
              <span className="storage-name">{shortage.storage_unit_name}</span>
            </div>
            
            <div className="quantity-info">
              <span className="quantity-value">{shortage.quantity} {shortage.unidad_medida_name}</span>
            </div>
            
            <div className="time-info">
              <span className="creation-date">
                {new Date(shortage.local_timestamp).toLocaleDateString('es-ES', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
              
              {shortage.solved && (
                <span className="resolution-date">
                  • Resuelto
                </span>
              )}
              
              <span className={`elapsed-time ${shortage.solved ? 'resolved' : 'pending'}`}>
                {formatTimeElapsed(shortage.local_timestamp)}
              </span>
            </div>
            
            <div className="status-actions">
              <span className={`status-badge ${shortage.solved ? 'resolved' : 'pending'}`}>
                {shortage.solved ? 'Resuelto' : 'Pendiente'}
              </span>
              
              {!shortage.solved && (
                <button
                  className="resolve-btn-shortage"
                  onClick={() => handleResolveShortage(shortage.shortage_id)}
                  disabled={resolvingShortages.has(shortage.shortage_id)}
                  title="Marcar como resuelto"
                >
                  {resolvingShortages.has(shortage.shortage_id) ? (
                    <span className="spinner" />
                  ) : (
                    <HiCheckCircle />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="stock-shortages-container">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      
      <div className="mp-toolbar flex flex-wrap items-center gap-3 mb-3">
        {/* Search */}
        <div className="stock-search-inline">
          <HiMagnifyingGlass className="search-icon" />
          <input
            type="text"
            placeholder="Buscar quiebres..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Filter dropdowns */}
        <div className="min-w-[140px]">
          <CheckboxMultiSelect
            options={storageOptions}
            value={selectedStorageUnits}
            onChange={(selected: any) => setSelectedStorageUnits(selected)}
            placeholder="Almacenamiento"
          />
        </div>

        {/* Date range */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="stock-toolbar-date"
          />
          <span className="date-separator">–</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="stock-toolbar-date"
          />
        </div>

        {/* Actions — right-aligned */}
        <div className="flex items-center gap-2 ml-auto">
          <button
            className="mp-toolbar-btn"
            onClick={handleExportToExcel}
            disabled={isLoading || filteredData.length === 0}
            title="Exportar quiebres a Excel"
          >
            <HiArrowDownTray className="w-4 h-4" />
            Excel
          </button>
        </div>
      </div>


      {/* Content */}
      <div className="shortages-content">
        {isLoading ? (
          <LoadingSpinner text="Cargando quiebres de stock..." transparent={true} />
        ) : (
          renderShortageList()
        )}
      </div>
    </div>
  );
};

export default StockShortages;